import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
    getContentTreeForProject,
    getTask,
    updateTask,
    updateTaskApproval,
    deleteTask,
    getSocialAccounts,
    publishSocialPostNow,
    selectImagesForTask,
    updateHeroImage,
    generateAiHeroImage,
    PublishSocialPostNowRequest,
    OverlayState,
} from '../../services/api';
import SocialAssetEditor from '../shared/SocialAssetEditor';
import TextOverlayModal from '../shared/TextOverlayModal';
import SocialPreviewModal from './SocialPreviewModal';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import {
    FileText,
    Share2,
    Search,
    Mail,
    CalendarDays,
    CheckCircle,
    Clock,
    XCircle,
    Circle,
    ThumbsUp,
    ThumbsDown,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    Trash2,
    Loader2,
    Linkedin,
    Twitter,
    Instagram,
    Facebook,
    Send,
    Calendar,
    Pencil,
    Eye,
} from 'lucide-react';
import CatalogueMenuBar from '../CatalogueMenuBar';
import './ContentBrowser.css';

// Remembers the user's scope choice for the browser session so the cascade dialog
// is only shown once. Resets on page refresh. Module-level (outside React) to survive
// modal unmount/remount. Migrated from SocialPostEditModal.tsx (Phase 3).
let sessionImageScopeDefault: 'all-cascade' | 'all' | 'platform' | null = null;

type ContentView = 'articles' | 'social' | 'seo' | 'newsletters' | 'calendars';
type ApprovalFilter = 'all' | 'approved' | 'pending' | 'rejected' | 'not_reviewed';
type SocialPlatform = 'linkedin' | 'twitter' | 'instagram' | 'facebook' | 'short_form' | 'pull_quotes' | 'carousel' | 'video_script';

interface SocialContentItem {
    task_id: string;
    title: string;
    source_article?: string;
    synthesized_result?: string | null;
    approval_status?: string | null;
    source_hero_image_url?: string | null;
}

interface SocialMultichannelData {
    social: SocialContentItem[];
    carousels: SocialContentItem[];
    videos: SocialContentItem[];
    pullQuotes: SocialContentItem[];
}

interface ContentBrowserProps {
    view: ContentView;
}

interface ContentContextType {
    selectedProjectId: string | undefined;
    onProjectSelect: (id: string | undefined) => void;
}

interface ContentItem {
    task_id: string;
    title: string;
    status?: string;
    has_image?: boolean;
    quick_wins?: string[];
    source_article?: string;
    approval_status?: string | null;
}

interface StreamGroup {
    name: string;
    article_count: number;
    articles: ContentItem[];
}

const VIEW_CONFIG: Record<ContentView, { title: string; icon: React.FC<any>; emptyMessage: string }> = {
    articles: { title: 'Articles by Stream', icon: FileText, emptyMessage: 'No articles found. Generate content from the Creator.' },
    social: { title: 'Social Content', icon: Share2, emptyMessage: 'No social content generated yet. Use Generate → Social Media Posts.' },
    seo: { title: 'SEO Metadata', icon: Search, emptyMessage: 'No SEO metadata generated yet.' },
    newsletters: { title: 'Emails & Newsletters', icon: Mail, emptyMessage: 'No emails or newsletters generated yet.' },
    calendars: { title: 'Content Calendars', icon: CalendarDays, emptyMessage: 'No content calendars generated yet.' },
};

const ApprovalBadge: React.FC<{ status?: string | null }> = ({ status }) => {
    switch (status) {
        case 'approved':
            return <span className="approval-badge approved" title="Approved"><CheckCircle size={14} /></span>;
        case 'pending':
            return <span className="approval-badge pending" title="Pending Review"><Clock size={14} /></span>;
        case 'rejected':
            return <span className="approval-badge rejected" title="Rejected"><XCircle size={14} /></span>;
        default:
            return <span className="approval-badge not-reviewed" title="Not Reviewed"><Circle size={14} /></span>;
    }
};

// Platforms that support the Publish-now action. Hoisted to avoid re-allocation on each render.
const PUBLISHABLE_PLATFORMS: SocialPlatform[] = ['linkedin', 'twitter', 'instagram', 'facebook'];

// Platform icon map. JSX elements are constant — hoisted out of renderCards() to avoid re-creation.
const PLATFORM_ICON: Record<string, React.ReactNode> = {
    linkedin: <Linkedin size={14} />,
    twitter: <Twitter size={14} />,
    instagram: <Instagram size={14} />,
    facebook: <Facebook size={14} />,
    short_form: <Share2 size={14} />,
};

const ContentBrowser: React.FC<ContentBrowserProps> = ({ view }) => {
    const { selectedProjectId } = useOutletContext<ContentContextType>();
    const navigate = useNavigate();
    const [items, setItems] = useState<ContentItem[]>([]);
    const [streams, setStreams] = useState<StreamGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [expandedStreams, setExpandedStreams] = useState<Set<string>>(new Set());
    const [updatingApproval, setUpdatingApproval] = useState<string | null>(null);
    const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('all');
    const [socialData, setSocialData] = useState<SocialMultichannelData | null>(null);
    const [socialPlatform, setSocialPlatform] = useState<SocialPlatform>('linkedin');
    // Publish-now state — keyed by "${task_id}:${platform}:${account_id}"
    const [publishInProgress, setPublishInProgress] = useState<Record<string, boolean>>({});
    const [publishResult, setPublishResult] = useState<Record<string, { success: boolean; post_url?: string; error_message?: string }>>({});
    // Inline account picker — keyed by "${task_id}:${platform}"
    const [pickerOpen, setPickerOpen] = useState<Record<string, boolean>>({});
    const [pickerAccounts, setPickerAccounts] = useState<Record<string, any[]>>({});
    const [pickerSelected, setPickerSelected] = useState<Record<string, string>>({});
    // Edit / Preview modal state
    const [editModalState, setEditModalState] = useState<{
        item: any;
        platform: 'linkedin' | 'twitter' | 'instagram' | 'facebook';
        platformLabel: string;
        allPlatformData: Record<string, any>;
        heroImage?: any;   // per-platform hero_image object (preferred over source_hero_image_url)
        initialOverlayState?: any;  // pre-filled overlay_state from task.input_data, fetched on modal open
    } | null>(null);
    const [previewModalItem, setPreviewModalItem] = useState<any | null>(null);
    // Local approval state for the current edit modal (deferred-persist — written in onSave, not on toggle)
    const [localApprovalStatus, setLocalApprovalStatus] = useState<string | null>(null);
    // Overlay modal state — null when not showing, set when "Add Overlay" is clicked inside editor
    const [overlayModalState, setOverlayModalState] = useState<{
        taskId: string;
        baseImageUrl: string;
        initialState: OverlayState | null;
        fallbackVisualPrompt?: string;
        projectId?: string;
    } | null>(null);
    // Ref so onSave (stable closure) can read the current approval status without stale capture
    const localApprovalStatusRef = useRef<string | null>(null);

    const config = VIEW_CONFIG[view];

    const loadData = useCallback(async () => {
        if (!selectedProjectId) return;
        setLoading(true);
        try {
            const tree = await getContentTreeForProject(selectedProjectId);

            if (view === 'articles') {
                setStreams(tree.streams);
                setExpandedStreams(new Set(tree.streams.map(s => s.name)));
            } else if (view === 'social') {
                setSocialData({
                    social: (tree.quick_wins as any).social_media || [],
                    carousels: (tree.quick_wins as any).multichannel_carousel || [],
                    videos: (tree.quick_wins as any).multichannel_video_script || [],
                    pullQuotes: (tree.quick_wins as any).multichannel_pull_quotes || [],
                });
            } else {
                const qwKey = view === 'seo' ? 'meta_data'
                    : view === 'newsletters' ? 'email_newsletter'
                    : 'content_calendar';
                setItems((tree.quick_wins as any)[qwKey] || []);
            }
        } catch (err) {
            console.error('Failed to load content tree:', err);
        } finally {
            setLoading(false);
        }
    }, [selectedProjectId, view]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Keep localApprovalStatusRef in sync with the state value so stable closures (onSave)
    // can read the current value without stale captures.
    useEffect(() => {
        localApprovalStatusRef.current = localApprovalStatus;
    }, [localApprovalStatus]);

    // Reset local approval state when a new edit modal opens.
    useEffect(() => {
        if (editModalState) {
            setLocalApprovalStatus(editModalState.item.approval_status ?? null);
        }
    }, [editModalState?.item?.task_id]);

    const handleDelete = async (taskId: string, title: string) => {
        if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
        try {
            await deleteTask(taskId);
            if (view === 'articles') {
                setStreams(prev => prev.map(s => ({
                    ...s,
                    articles: s.articles.filter(a => a.task_id !== taskId)
                })));
            } else if (view === 'social') {
                setSocialData(prev => prev ? { ...prev, social: prev.social.filter(i => i.task_id !== taskId) } : null);
            } else {
                setItems(prev => prev.filter(i => i.task_id !== taskId));
            }
        } catch (err) {
            console.error('Failed to delete task:', err);
            alert('Failed to delete. Please try again.');
        }
    };

    const handleApproval = async (taskId: string, status: string) => {
        setUpdatingApproval(taskId);
        try {
            await updateTaskApproval(taskId, status);
            if (view === 'articles') {
                setStreams(prev => prev.map(s => ({
                    ...s,
                    articles: s.articles.map(a =>
                        a.task_id === taskId ? { ...a, approval_status: status } : a
                    )
                })));
            } else if (view === 'social') {
                setSocialData(prev => prev ? {
                    ...prev,
                    social: prev.social.map(i => i.task_id === taskId ? { ...i, approval_status: status } : i),
                } : null);
            } else {
                setItems(prev => prev.map(i =>
                    i.task_id === taskId ? { ...i, approval_status: status } : i
                ));
            }
        } catch (err) {
            console.error('Failed to update approval:', err);
        } finally {
            setUpdatingApproval(null);
        }
    };

    const toggleStream = (name: string) => {
        setExpandedStreams(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const openPreview = (taskId: string) => {
        window.open(`/preview/${taskId}`, '_blank');
    };

    const handleTogglePicker = async (taskId: string, platform: string) => {
        const key = `${taskId}:${platform}`;
        if (pickerOpen[key]) {
            setPickerOpen(prev => ({ ...prev, [key]: false }));
            return;
        }
        // Fetch accounts if not already loaded
        if (!pickerAccounts[key]) {
            try {
                const accounts = await getSocialAccounts(selectedProjectId);
                const filtered = (accounts || []).filter(
                    (a: any) => a.platform === platform && a.status === 'active'
                );
                setPickerAccounts(prev => ({ ...prev, [key]: filtered }));
                if (filtered.length > 0) {
                    setPickerSelected(prev => ({ ...prev, [key]: filtered[0].account_id }));
                }
            } catch {
                setPickerAccounts(prev => ({ ...prev, [key]: [] }));
            }
        }
        setPickerOpen(prev => ({ ...prev, [key]: true }));
    };

    const handlePublishNow = async (taskId: string, platform: string, postText: string, heroImageUrl?: string) => {
        const pickerKey = `${taskId}:${platform}`;
        const accountId = pickerSelected[pickerKey];
        if (!accountId) return;
        const inFlightKey = `${taskId}:${platform}:${accountId}`;
        setPublishInProgress(prev => ({ ...prev, [inFlightKey]: true }));
        setPickerOpen(prev => ({ ...prev, [pickerKey]: false }));
        try {
            const result = await publishSocialPostNow({
                task_id: taskId,
                platform: platform as PublishSocialPostNowRequest['platform'],
                social_account_id: accountId,
                content: postText,
                hero_image_url: heroImageUrl,
            });
            setPublishResult(prev => ({ ...prev, [inFlightKey]: result }));
        } catch (err: any) {
            setPublishResult(prev => ({
                ...prev,
                [inFlightKey]: { success: false, error_message: err?.response?.data?.detail || 'Publish failed' },
            }));
        } finally {
            setPublishInProgress(prev => ({ ...prev, [inFlightKey]: false }));
        }
    };

    const matchesFilter = (item: ContentItem) => {
        if (approvalFilter === 'all') return true;
        if (approvalFilter === 'not_reviewed') return !item.approval_status;
        return item.approval_status === approvalFilter;
    };

    const filteredItems = useMemo(() => items.filter(matchesFilter), [items, approvalFilter]);

    const filteredStreams = useMemo(() =>
        streams.map(s => ({ ...s, articles: s.articles.filter(matchesFilter) })),
        [streams, approvalFilter]
    );

    const IconComponent = config.icon;

    if (!selectedProjectId) {
        return (
            <div className="content-browser">
                <div className="content-browser-header">
                    <IconComponent size={24} />
                    <h2>{config.title}</h2>
                </div>
                <GlassCard className="content-browser-empty">
                    <p>Select a project to view content.</p>
                </GlassCard>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="content-browser">
                <div className="content-browser-header">
                    <IconComponent size={24} />
                    <h2>{config.title}</h2>
                </div>
                <div className="content-browser-loading">
                    <Loader2 size={24} className="spin" />
                    <span>Loading content...</span>
                </div>
            </div>
        );
    }

    const renderItemRow = (item: ContentItem) => (
        <div key={item.task_id} className="content-browser-item">
            <ApprovalBadge status={item.approval_status} />
            <div className="content-browser-item-info" onClick={() => openPreview(item.task_id)}>
                <span className="content-browser-item-title">{item.title}</span>
                {item.source_article && (
                    <span className="content-browser-item-source">from: {item.source_article}</span>
                )}
            </div>
            <div className="approval-actions">
                <button
                    className={`approval-btn approve ${item.approval_status === 'approved' ? 'active' : ''}`}
                    onClick={() => handleApproval(item.task_id, item.approval_status === 'approved' ? 'pending' : 'approved')}
                    disabled={updatingApproval === item.task_id}
                    title="Approve"
                >
                    <ThumbsUp size={14} />
                </button>
                <button
                    className={`approval-btn reject ${item.approval_status === 'rejected' ? 'active' : ''}`}
                    onClick={() => handleApproval(item.task_id, item.approval_status === 'rejected' ? 'pending' : 'rejected')}
                    disabled={updatingApproval === item.task_id}
                    title="Reject"
                >
                    <ThumbsDown size={14} />
                </button>
                <button
                    className="approval-btn preview"
                    onClick={() => openPreview(item.task_id)}
                    title="Open preview"
                >
                    <ExternalLink size={14} />
                </button>
                <button
                    className="approval-btn delete"
                    onClick={() => handleDelete(item.task_id, item.title)}
                    title="Delete"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );

    const hasContent = view === 'articles'
        ? streams.some(s => s.articles.length > 0)
        : view === 'social'
        ? ((socialData?.social.length ?? 0) > 0 || (socialData?.carousels.length ?? 0) > 0
            || (socialData?.videos.length ?? 0) > 0 || (socialData?.pullQuotes.length ?? 0) > 0)
        : items.length > 0;

    const filteredTotal = view === 'articles'
        ? filteredStreams.reduce((sum, s) => sum + s.articles.length, 0)
        : view === 'social' ? 0
        : filteredItems.length;
    const unfilteredTotal = view === 'articles'
        ? streams.reduce((sum, s) => sum + s.articles.length, 0)
        : view === 'social' ? 0
        : items.length;

    const filterOptions: { value: ApprovalFilter; label: string }[] = [
        { value: 'all', label: 'All' },
        { value: 'approved', label: 'Approved' },
        { value: 'pending', label: 'Pending' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'not_reviewed', label: 'Not Reviewed' },
    ];

    const renderSocialView = () => {
        if (!socialData) return null;

        const PLATFORM_TABS: { key: SocialPlatform; label: string; count: number }[] = ([
            { key: 'linkedin'     as SocialPlatform, label: 'LinkedIn',      count: socialData.social.length },
            { key: 'twitter'      as SocialPlatform, label: 'Twitter/X',     count: socialData.social.length },
            { key: 'instagram'    as SocialPlatform, label: 'Instagram',     count: socialData.social.length },
            { key: 'facebook'     as SocialPlatform, label: 'Facebook',      count: socialData.social.length },
            { key: 'short_form'   as SocialPlatform, label: 'Short Form',    count: socialData.social.length },
            { key: 'pull_quotes'  as SocialPlatform, label: 'Pull Quotes',   count: socialData.pullQuotes.length },
            { key: 'carousel'     as SocialPlatform, label: 'Carousels',     count: socialData.carousels.length },
            { key: 'video_script' as SocialPlatform, label: 'Video Scripts', count: socialData.videos.length },
        ] as { key: SocialPlatform; label: string; count: number }[]).filter(t => t.count > 0);

        if (PLATFORM_TABS.length === 0) {
            return (
                <GlassCard className="content-browser-empty">
                    <p>{config.emptyMessage}</p>
                </GlassCard>
            );
        }

        const activePlatform = PLATFORM_TABS.some(t => t.key === socialPlatform)
            ? socialPlatform
            : PLATFORM_TABS[0].key;

        const renderCards = () => {
            if (activePlatform === 'pull_quotes') {
                return socialData.pullQuotes.map(item => {
                    let quotes: string[] = [];
                    try { quotes = JSON.parse(item.synthesized_result || '[]'); } catch {}
                    if (!quotes.length) return null;
                    return (
                        <GlassCard key={item.task_id} className="social-content-card">
                            <div className="social-card-source">{item.source_article || item.title}</div>
                            <div className="social-pull-quotes">
                                {quotes.map((q, i) => (
                                    <blockquote key={i} className="social-pull-quote">"{q}"</blockquote>
                                ))}
                            </div>
                        </GlassCard>
                    );
                });
            }

            if (activePlatform === 'carousel') {
                return socialData.carousels.map(item => {
                    let data: any = {};
                    try { data = JSON.parse(item.synthesized_result || '{}'); } catch {}
                    return (
                        <GlassCard key={item.task_id} className="social-content-card">
                            <div className="social-card-source">{item.source_article || item.title}</div>
                            <div className="social-carousel">
                                {data.title && <div className="social-carousel-title">{data.title}</div>}
                                {data.subtitle && <div className="social-carousel-subtitle">{data.subtitle}</div>}
                                {(data.slides || []).map((slide: any) => (
                                    <div key={slide.slide_number} className="social-carousel-slide">
                                        <span className="slide-num">{slide.slide_number}</span>
                                        <div>
                                            <div className="slide-headline">{slide.headline}</div>
                                            <div className="slide-body">{slide.body}</div>
                                        </div>
                                    </div>
                                ))}
                                {data.closing_cta && <div className="social-carousel-cta">{data.closing_cta}</div>}
                            </div>
                        </GlassCard>
                    );
                });
            }

            if (activePlatform === 'video_script') {
                return socialData.videos.map(item => {
                    let data: any = {};
                    try { data = JSON.parse(item.synthesized_result || '{}'); } catch {}
                    return (
                        <GlassCard key={item.task_id} className="social-content-card">
                            <div className="social-card-source">{item.source_article || item.title}</div>
                            <div className="social-video-script">
                                {data.hook && (
                                    <div className="social-script-section">
                                        <div className="social-script-label">Hook</div>
                                        <p>{data.hook}</p>
                                    </div>
                                )}
                                {(data.talking_points || []).map((tp: string, i: number) => (
                                    <div key={i} className="social-script-section">
                                        <div className="social-script-label">Point {i + 1}</div>
                                        <p>{tp}</p>
                                    </div>
                                ))}
                                {data.cta && (
                                    <div className="social-script-section">
                                        <div className="social-script-label">CTA</div>
                                        <p>{data.cta}</p>
                                    </div>
                                )}
                            </div>
                        </GlassCard>
                    );
                });
            }

            // Platform posts: linkedin, twitter, instagram, facebook, short_form
            // platformKey maps directly to the SOCIAL_POSTS_SCHEMA key in synthesized_result.
            // Note: no 'twitter_thread' mapping — SOCIAL_POSTS_SCHEMA emits 'twitter'.
            const platformKey = activePlatform;
            const platformLabel = PLATFORM_TABS.find(t => t.key === activePlatform)?.label ?? activePlatform;
            return socialData.social.map(item => {
                let data: any = {};
                try { data = JSON.parse(item.synthesized_result || '{}'); } catch {}
                const content = data[platformKey];
                if (!content) return null;
                // content may be a plain string (old schema) or {post, hero_image, ...} (new schema)
                // or a structured object {post|caption, cta, hashtags[]} from multichannel path
                const postObj = typeof content === 'object' && content !== null ? content : null;
                const heroImage = postObj?.hero_image;
                const heroUrl = heroImage?.urls?.large2x || heroImage?.urls?.large || item.source_hero_image_url || undefined;
                const renderText = typeof content === 'string'
                    ? content
                    : [
                        postObj?.post || postObj?.caption || '',
                        postObj?.cta || '',
                        Array.isArray(postObj?.hashtags)
                            ? postObj.hashtags.map((t: string) => `#${String(t).replace(/^#/, '')}`).join(' ')
                            : '',
                    ].filter(Boolean).join('\n\n');
                return (
                    <GlassCard key={item.task_id} className="asset-card social-content-card">
                        {heroUrl && (
                            <img src={heroUrl} alt="" className="asset-card-image" />
                        )}
                        <div className="social-card-body">
                            <div className="asset-card-header">
                                <div className="asset-card-type">
                                    {PLATFORM_ICON[activePlatform]}
                                    {platformLabel}
                                </div>
                                <ApprovalBadge status={item.approval_status} />
                            </div>
                            <div className="social-card-source">{item.source_article || item.title}</div>
                            <p className="asset-card-preview">{renderText}</p>
                            <div className="asset-card-actions">
                                <div className="asset-card-actions-row">
                                    {PUBLISHABLE_PLATFORMS.includes(activePlatform as any) ? (
                                        <>
                                            <button
                                                className="approval-btn"
                                                onClick={async () => {
                                                    // Open modal immediately with available data,
                                                    // then fetch the full task to resolve overlay_state
                                                    // from task.input_data (not available in the content
                                                    // tree response — BLOCKER-002).
                                                    const baseState = {
                                                        item,
                                                        platform: activePlatform as 'linkedin' | 'twitter' | 'instagram' | 'facebook',
                                                        platformLabel,
                                                        allPlatformData: data,
                                                        heroImage: heroImage,
                                                        initialOverlayState: null as any,
                                                    };
                                                    setEditModalState(baseState);
                                                    try {
                                                        const fullTask = await getTask(item.task_id);
                                                        const overlay = fullTask?.input_data?.overlay_state ?? null;
                                                        if (overlay) {
                                                            setEditModalState(prev => prev ? { ...prev, initialOverlayState: overlay } : prev);
                                                        }
                                                    } catch {
                                                        // Non-fatal: overlay pre-fill unavailable, user can still
                                                        // set controls manually.
                                                    }
                                                }}
                                                title="Edit this post"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            <button
                                                className="approval-btn"
                                                onClick={() => setPreviewModalItem(item)}
                                                title="Preview all platforms"
                                            >
                                                <Eye size={14} />
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            className="approval-btn preview"
                                            onClick={() => openPreview(item.task_id)}
                                            title="Open preview"
                                        >
                                            <ExternalLink size={14} />
                                        </button>
                                    )}
                                    <button
                                        className={`approval-btn approve ${item.approval_status === 'approved' ? 'active' : ''}`}
                                        onClick={() => handleApproval(item.task_id, item.approval_status === 'approved' ? 'pending' : 'approved')}
                                        disabled={updatingApproval === item.task_id}
                                        title="Approve article — approves all four platform posts for this article"
                                    >
                                        <ThumbsUp size={14} />
                                    </button>
                                    {selectedProjectId && (
                                        <button
                                            className="approval-btn"
                                            onClick={() => navigate(`/scheduling/${selectedProjectId}`)}
                                            title="View in scheduling calendar"
                                        >
                                            <Calendar size={14} />
                                        </button>
                                    )}
                                    <button
                                        className="approval-btn delete"
                                        onClick={() => handleDelete(item.task_id, item.title)}
                                        title="Delete"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                                {PUBLISHABLE_PLATFORMS.includes(activePlatform as any) && (
                                    <div className="asset-card-actions-row">
                                        <GlassButton
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleTogglePicker(item.task_id, activePlatform)}
                                            title="Publish now"
                                        >
                                            <Send size={13} /> Publish now
                                        </GlassButton>
                                    </div>
                                )}
                                {(() => {
                                    const pickerKey = `${item.task_id}:${activePlatform}`;
                                    const accountId = pickerSelected[pickerKey] || '';
                                    // Guard: when no account is selected yet accountId is '' — use that
                                    // rather than 'undefined' so state keys stay well-formed strings.
                                    const inFlightKey = `${item.task_id}:${activePlatform}:${accountId}`;
                                    const accounts = pickerAccounts[pickerKey] || [];
                                    const result = accountId ? publishResult[inFlightKey] : undefined;
                                    return (
                                        <>
                                            {pickerOpen[pickerKey] && (
                                                <div className="social-publish-picker">
                                                    {accounts.length === 0 ? (
                                                        <span className="social-publish-picker-empty">
                                                            No connected {activePlatform} accounts —{' '}
                                                            <a href="/social-accounts">connect one</a>
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <select
                                                                className="social-publish-picker-select"
                                                                value={pickerSelected[pickerKey] || ''}
                                                                onChange={e => setPickerSelected(prev => ({ ...prev, [pickerKey]: e.target.value }))}
                                                            >
                                                                {accounts.map((a: any) => (
                                                                    <option key={a.account_id} value={a.account_id}>
                                                                        {a.display_name || a.username || a.account_id}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <GlassButton
                                                                variant="primary"
                                                                size="sm"
                                                                disabled={publishInProgress[inFlightKey]}
                                                                onClick={() => handlePublishNow(item.task_id, activePlatform, renderText, heroUrl)}
                                                            >
                                                                {publishInProgress[inFlightKey] ? 'Publishing…' : 'Publish'}
                                                            </GlassButton>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {result && (
                                                <div className={`social-publish-result ${result.success ? 'success' : 'error'}`}>
                                                    {result.success
                                                        ? result.post_url
                                                            ? <a href={result.post_url} target="_blank" rel="noreferrer">View live post</a>
                                                            : 'Published successfully'
                                                        : result.error_message || 'Publish failed'}
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    </GlassCard>
                );
            });
        };

        const cards = renderCards()?.filter(Boolean) ?? [];

        return (
            <>
                <div className="social-platform-tabs">
                    {PLATFORM_TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`social-platform-tab ${activePlatform === tab.key ? 'active' : ''}`}
                            onClick={() => setSocialPlatform(tab.key)}
                        >
                            {tab.label}
                            <span className="social-tab-count">{tab.count}</span>
                        </button>
                    ))}
                </div>
                {cards.length === 0 ? (
                    <GlassCard className="content-browser-empty">
                        <p>No content generated for this format yet.</p>
                    </GlassCard>
                ) : (
                    <div className="assets-grid">{cards}</div>
                )}
            </>
        );
    };

    return (
        <div className="content-browser">
            <div className="content-browser-header">
                <IconComponent size={24} />
                <h2>{config.title}</h2>
                {hasContent && view !== 'social' && (
                    <div className="content-browser-filter-group">
                        {filterOptions.map(opt => (
                            <button
                                key={opt.value}
                                className={`filter-pill ${approvalFilter === opt.value ? 'active' : ''} ${opt.value}`}
                                onClick={() => setApprovalFilter(opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}
                {hasContent && view !== 'social' && (
                    <span className="content-browser-count">
                        {approvalFilter !== 'all'
                            ? `${filteredTotal} of ${unfilteredTotal}`
                            : `${unfilteredTotal}`}
                        {view === 'articles' ? ' articles' : ' items'}
                    </span>
                )}
            </div>

            {view === 'social' ? (
                renderSocialView()
            ) : !hasContent ? (
                <GlassCard className="content-browser-empty">
                    <p>{config.emptyMessage}</p>
                </GlassCard>
            ) : filteredTotal === 0 ? (
                <GlassCard className="content-browser-empty">
                    <p>No {approvalFilter === 'not_reviewed' ? 'unreviewed' : approvalFilter} content found.</p>
                </GlassCard>
            ) : view === 'articles' ? (
                <>
                    <CatalogueMenuBar
                        projectId={selectedProjectId}
                        streamNames={streams.map(s => s.name)}
                        onComplete={loadData}
                    />
                    <div className="content-browser-streams">
                        {filteredStreams.filter(s => s.articles.length > 0).map(stream => (
                            <GlassCard key={stream.name} className="content-browser-stream">
                                <div
                                    className="content-browser-stream-header"
                                    onClick={() => toggleStream(stream.name)}
                                >
                                    {expandedStreams.has(stream.name) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    <span className="stream-name">{stream.name}</span>
                                    <span className="stream-count">{stream.articles.length}</span>
                                </div>
                                {expandedStreams.has(stream.name) && (
                                    <div className="content-browser-list">
                                        {stream.articles.map(renderItemRow)}
                                    </div>
                                )}
                            </GlassCard>
                        ))}
                    </div>
                </>
            ) : (
                <div className="content-browser-list">
                    {filteredItems.map(renderItemRow)}
                </div>
            )}

            {/* Per-platform edit modal — uses shared SocialAssetEditor (Phase 3 migration) */}
            {editModalState && (() => {
                const taskId = editModalState.item.task_id;
                const platform = editModalState.platform;
                const allPlatformData = editModalState.allPlatformData;

                // Derive the initial post text for this platform
                const platformEntry = allPlatformData[platform];
                const initialText: string = !platformEntry
                    ? ''
                    : typeof platformEntry === 'string'
                    ? platformEntry
                    : platformEntry.post || platformEntry.caption || '';

                // Resolve the displayed hero URL (same precedence as card rendering)
                const displayHeroUrl: string | undefined =
                    editModalState.heroImage?.urls?.large2x ||
                    editModalState.heroImage?.urls?.large ||
                    editModalState.item.source_hero_image_url ||
                    undefined;

                // Exclude current image from Pexels re-fetch
                const excludedIds: number[] = editModalState.heroImage?.id
                    ? [editModalState.heroImage.id]
                    : [];

                // Seed AI prompt / Pexels query from hero description or source article title
                const imageConcept: string =
                    editModalState.heroImage?.description ||
                    editModalState.item.source_article ||
                    editModalState.item.title ||
                    '';

                // onSave: synthesise the updated synthesized_result JSON, persist approval if changed.
                // applyImageToAll is derived from sessionImageScopeDefault (set in onResolveScope).
                // This function is called by SocialAssetEditor after scope is resolved.
                const handleSave = async ({ content, imageUrl: savedImageUrl }: {
                    content: string | Record<string, string>;
                    title?: string;
                    imageUrl?: string;
                }) => {
                    // Extract the post text. Content is a string (single-field platform) or an object.
                    const postText = typeof content === 'string'
                        ? content
                        : (content as any).post
                            || (content as any).intro_text
                            || (content as any).tweet_text
                            || (content as any).caption
                            || (content as any).post_text
                            || '';

                    const applyToAll =
                        sessionImageScopeDefault === 'all' ||
                        sessionImageScopeDefault === 'all-cascade';

                    const updated: Record<string, any> = { ...allPlatformData };

                    // Propagate text to active platform; propagate hero to all/platform per scope.
                    for (const key of Object.keys(updated)) {
                        const entry = updated[key];
                        if (typeof entry !== 'object' || entry === null) continue;
                        const isActive = key === platform;
                        const newHero = savedImageUrl
                            ? { urls: { large2x: savedImageUrl, large: savedImageUrl, original: savedImageUrl } }
                            : entry.hero_image ?? null;
                        updated[key] = {
                            ...entry,
                            ...(isActive ? { post: postText } : {}),
                            ...((isActive || applyToAll) && savedImageUrl
                                ? { hero_image: newHero }
                                : {}),
                        };
                    }
                    // Fallback: active platform was a plain string entry
                    if (typeof updated[platform] !== 'object' || updated[platform] === null) {
                        updated[platform] = {
                            post: postText,
                            ...(savedImageUrl ? { hero_image: { urls: { large2x: savedImageUrl, large: savedImageUrl, original: savedImageUrl } } } : {}),
                        };
                    }

                    const newSynthesized = JSON.stringify(updated);
                    await updateTask(taskId, { synthesized_result: newSynthesized });

                    // Approval is written here (not on toggle) so Cancel truly discards the change.
                    const currentApproval = localApprovalStatusRef.current;
                    const originalApproval = editModalState.item.approval_status ?? null;
                    // Normalise null → 'pending' consistently: the API treats null as 'pending',
                    // so write 'pending' in both places to avoid a null/pending mismatch on the card.
                    const persistedApproval: 'pending' | 'approved' = currentApproval === 'approved' ? 'approved' : 'pending';
                    if (currentApproval !== originalApproval) {
                        await updateTaskApproval(taskId, persistedApproval);
                    }

                    // Update card in socialData so UI reflects changes immediately.
                    // Use persistedApproval (never null) to match what was written to the API.
                    setSocialData(prev => prev ? {
                        ...prev,
                        social: prev.social.map(i =>
                            i.task_id === taskId
                                ? { ...i, synthesized_result: newSynthesized, approval_status: persistedApproval }
                                : i
                        ),
                    } : null);
                };

                return (
                    <SocialAssetEditor
                        platform={platform}
                        platformLabel={editModalState.platformLabel}
                        initialContent={initialText}
                        initialImageUrl={displayHeroUrl}
                        initialImageConcept={imageConcept}
                        persistImageImmediately={true}
                        onImageChangeImmediate={async (url, source) => {
                            await updateHeroImage(taskId, url, '', source);
                        }}
                        fetchPexels={async (_query) => {
                            // selectImagesForTask uses article-derived search terms server-side;
                            // the user query cannot be forwarded, so pexelsQueryDisabled is set.
                            const result = await selectImagesForTask(taskId, true, excludedIds);
                            const imageUrl =
                                result.images?.hero?.urls?.large2x ||
                                result.images?.hero?.urls?.large ||
                                '';
                            return { image_url: imageUrl };
                        }}
                        pexelsQueryDisabled={true}
                        generateAI={async (prompt) => {
                            const res = await generateAiHeroImage(prompt, editModalState?.item.title);
                            return { image_url: res.url };
                        }}
                        uploadImage={async (file) => {
                            // Upload via brand-guides asset endpoint (same pattern as ContentPreview.tsx)
                            const formData = new FormData();
                            formData.append('file', file);
                            const resp = await fetch('/api/brand-guides/upload-asset', {
                                method: 'POST',
                                headers: {
                                    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                                },
                                body: formData,
                            });
                            if (!resp.ok) throw new Error('Upload failed');
                            const data = await resp.json();
                            const assetUrl: string = data.url || data.asset_url || data.file_url || '';
                            if (!assetUrl) throw new Error('No URL returned from upload');
                            // updateHeroImage is called via onImageChangeImmediate after this returns
                            return { image_url: assetUrl };
                        }}
                        cascade={{
                            scopeChoice: sessionImageScopeDefault,
                            onResolveScope: async (choice) => {
                                sessionImageScopeDefault = choice;
                                // Cascade PUT: if 'all-cascade', update the source article's hero too.
                                if (choice === 'all-cascade' && displayHeroUrl) {
                                    try {
                                        await updateHeroImage(
                                            taskId,
                                            displayHeroUrl,
                                            editModalState.heroImage?.photographer || '',
                                            editModalState.heroImage?.source || 'custom',
                                            { cascade: true },
                                        );
                                    } catch (cascadeErr) {
                                        // Non-fatal — primary save continues regardless
                                        console.warn('[ContentBrowser] cascade PUT failed:', cascadeErr);
                                    }
                                }
                            },
                        }}
                        approval={{
                            status: (localApprovalStatus === 'approved' ? 'approved' : 'pending') as 'approved' | 'pending',
                            onToggle: () => {
                                setLocalApprovalStatus(prev => (prev === 'approved' ? null : 'approved'));
                            },
                        }}
                        overlay={{
                            onOpen: () => {
                                // Snapshot current overlay state and open TextOverlayModal.
                                // SocialAssetEditor calls onClose() after this, closing the editor.
                                setOverlayModalState({
                                    taskId,
                                    baseImageUrl:
                                        (editModalState.initialOverlayState as OverlayState | undefined)?.base_image_url ||
                                        displayHeroUrl ||
                                        '',
                                    initialState: editModalState.initialOverlayState ?? null,
                                    fallbackVisualPrompt: imageConcept || undefined,
                                    projectId: selectedProjectId ?? undefined,
                                });
                            },
                        }}
                        onSave={handleSave}
                        onClose={() => setEditModalState(null)}
                    />
                );
            })()}

            {/* TextOverlayModal — opened when "Add Overlay" is clicked inside the editor */}
            {overlayModalState && (
                <TextOverlayModal
                    baseImageUrl={overlayModalState.baseImageUrl}
                    initialOverlayState={overlayModalState.initialState}
                    fallbackVisualPrompt={overlayModalState.fallbackVisualPrompt}
                    projectId={overlayModalState.projectId}
                    onClose={() => setOverlayModalState(null)}
                    onApplied={async ({ url, overlay_state }: { url: string; overlay_state: OverlayState }) => {
                        try {
                            await updateHeroImage(overlayModalState.taskId, url, '', 'overlay-composite');
                            const latestTask = await getTask(overlayModalState.taskId);
                            const newInputData = { ...(latestTask.input_data || {}), overlay_state };
                            await updateTask(overlayModalState.taskId, { input_data: newInputData });
                        } catch (e) {
                            console.error('[ContentBrowser] Failed to persist overlay:', e);
                        }
                        setOverlayModalState(null);
                    }}
                />
            )}

            {/* Read-only all-platforms preview modal */}
            {previewModalItem && (
                <SocialPreviewModal
                    item={previewModalItem}
                    onClose={() => setPreviewModalItem(null)}
                />
            )}
        </div>
    );
};

export default ContentBrowser;
